import type { RunStats } from './data/types';

export const PAY_PER_CORRECT = 5;
export const AUDIT_BONUS = 10;
export const MISTAKE_PENALTY = 5;
export const FREE_MISTAKES_BASE = 5;
export const MISTAKES_FIRED = 5;
export const NEW_GAME_PLUS_MISTAKES_FIRED = 2;
export const NEW_GAME_PLUS_SCORE_MULTIPLIER = 2;

export interface Allocations {
  shareholder: number;
  bonus: number;
  lobbying: number;
}

export class Economy {
  credits = 0;
  mistakesToday = 0;
  freeMistakesToday = FREE_MISTAKES_BASE;
  alloc: Allocations = { shareholder: 0, bonus: 0, lobbying: 0 };
  readonly mistakesFired: number;
  readonly scoreMultiplier: number;
  stats: RunStats = {
    processed: 0,
    correctCount: 0,
    mistakes: 0,
    auditsCorrect: 0,
    auditsWrong: 0,
    greenlitCount: 0,
    sunsetCount: 0,
    discrepanciesFound: 0,
    bribesTaken: 0,
    creditsEarned: 0,
  };

  constructor(newGamePlus = false) {
    this.mistakesFired = newGamePlus ? NEW_GAME_PLUS_MISTAKES_FIRED : MISTAKES_FIRED;
    this.scoreMultiplier = newGamePlus ? NEW_GAME_PLUS_SCORE_MULTIPLIER : 1;
  }

  earn(amount: number): void {
    this.credits += amount;
    if (amount > 0) this.stats.creditsEarned += amount;
  }

  /** Returns penalty applied (0 while free warnings remain). */
  recordMistake(): number {
    this.mistakesToday += 1;
    this.stats.mistakes += 1;
    if (this.mistakesToday <= this.freeMistakesToday) return 0;
    const penalty = Math.min(this.credits, MISTAKE_PENALTY);
    this.credits -= penalty;
    return penalty;
  }

  get firedForMistakes(): boolean {
    return this.mistakesToday >= this.mistakesFired;
  }

  startDay(): void {
    this.mistakesToday = 0;
    // Every 5 lobbying credits buys one extra free mistake the next day.
    this.freeMistakesToday = FREE_MISTAKES_BASE + Math.floor(this.alloc.lobbying / 5);
  }

  allocate(pool: keyof Allocations, amount: number): void {
    this.alloc[pool] += amount;
    this.credits -= amount;
  }

  legacyScore(): number {
    const base =
      this.alloc.shareholder * 3 +
      this.alloc.bonus * 2 +
      this.alloc.lobbying +
      this.stats.correctCount * 5 -
      this.stats.mistakes * 3;
    return base * this.scoreMultiplier;
  }
}
